import { useCallback } from 'react';

import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useInsightFilter } from '@/modules/insights/contexts/InsightFilterContext';

interface ToolbarProps {
  onCreateInsight: () => void;
  insightsEnabled: boolean;
  onToggleInsights: (checked: boolean) => void;
  canWrite: boolean;
  readonly?: boolean;
}

/**
 * Toolbar component for the insights table
 * Provides search, the enable-insights toggle and the create action
 */
export const Toolbar = ({
  onCreateInsight,
  insightsEnabled,
  onToggleInsights,
  canWrite,
  readonly = false,
}: ToolbarProps) => {
  const { textFilter, setTextFilter } = useInsightFilter();
  const { t } = useTranslation();

  const handleSetTextFilter = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setTextFilter(value);
    },
    [setTextFilter]
  );

  return (
    <div className="mb-5 flex items-center justify-between gap-2 pl-1">
      <SearchBar
        placeholder={t('insights.search_insights')}
        value={textFilter}
        onChange={handleSetTextFilter}
      />
      <div className="flex items-center gap-4">
        <Switch
          disabled={!canWrite}
          label={t('insights.toggle_label')}
          id="insights-toggle"
          checked={insightsEnabled}
          onCheckedChange={onToggleInsights}
          aria-label={t('insights.toggle_label')}
        />
        <InfoTooltip content={readonly ? t('insights.disabled_tooltip') : ''}>
          <span>
            <Button
              onClick={onCreateInsight}
              variant="outline-neutral"
              disabled={readonly || !canWrite}
            >
              <PlusIcon size={16} />
              {t('common.create')}
            </Button>
          </span>
        </InfoTooltip>
      </div>
    </div>
  );
};

export default Toolbar;
